/**
 * Boolean env flags — ONE parser for every `SOME_FLAG=…` switch.
 *
 * Before this module the repo compared `process.env.X === 'true'` in seven
 * places and `=== '1'` in fourteen, with nothing documenting which spelling a
 * given flag wanted: an operator setting `DISABLE_IMAGE_GEN=1` (the spelling
 * most flags used) got a silent no-op while image generation kept billing.
 * Every flag now reads the same spellings, case-insensitively and
 * whitespace-tolerant:
 *
 *   enabled  → `1` `true` `yes` `on`
 *   disabled → `0` `false` `no` `off`
 *   anything else (unset, empty, a typo) → the caller's default
 *
 * Two entry points, on purpose:
 *
 *   - `isFlagEnabled('NAME')` reads `process.env[NAME]` by name. Server-side
 *     only in effect: a client bundle sees an empty `process.env`, so the
 *     flag reads as unset there (the same as before — non-public variables
 *     were never inlined).
 *   - `isFlagValueEnabled(process.env.NEXT_PUBLIC_NAME)` takes the VALUE.
 *     Next inlines `process.env.NEXT_PUBLIC_*` only when the read is the
 *     literal member expression, so public flags must pass that literal —
 *     never a name string.
 *
 * Leaf module: no imports, no `server-only`, so the Convex bundler, the
 * workflow bundler and client components can all share it.
 */

/**
 * Any env-like bag of strings. Deliberately NOT `NodeJS.ProcessEnv`: that type
 * requires `NODE_ENV`, which forces every caller passing a small literal (all
 * of the tests, and any future caller inspecting a subset) through an unsafe
 * cast. `process.env` is assignable to this.
 */
export type EnvLike = Readonly<Record<string, string | undefined>>

const ENABLED_SPELLINGS: ReadonlySet<string> = new Set([
  '1',
  'true',
  'yes',
  'on',
])
const DISABLED_SPELLINGS: ReadonlySet<string> = new Set([
  '0',
  'false',
  'no',
  'off',
])

export type FlagOptions = {
  /** What an unset or unrecognized value means. Defaults to `false`. */
  defaultValue?: boolean
}

/**
 * Parse one raw flag value. `undefined` means "not recognized" so callers can
 * apply their own default instead of treating a typo as `false`.
 */
export function parseFlagValue(
  raw: string | null | undefined
): boolean | undefined {
  if (raw === undefined || raw === null) {
    return
  }
  const normalized = raw.trim().toLowerCase()
  if (ENABLED_SPELLINGS.has(normalized)) {
    return true
  }
  if (DISABLED_SPELLINGS.has(normalized)) {
    return false
  }
}

/**
 * Read a flag from its VALUE. Use for `NEXT_PUBLIC_*` variables, passing the
 * literal `process.env.NEXT_PUBLIC_X` so Next can inline it.
 */
export function isFlagValueEnabled(
  raw: string | null | undefined,
  options?: FlagOptions
): boolean {
  return parseFlagValue(raw) ?? options?.defaultValue ?? false
}

/**
 * Read a server-side flag by NAME. Default-on kill switches pass
 * `{ defaultValue: true }`; an explicit disabled spelling still wins.
 */
export function isFlagEnabled(
  name: string,
  options?: FlagOptions & { env?: EnvLike }
): boolean {
  const env = options?.env ?? process.env
  return isFlagValueEnabled(env[name], options)
}
